import { HttpParams } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CsvDownloadService } from '@netfoundry-ui/feature/shared-services';
import { PlatformServiceService } from '@netfoundry-ui/shared/apiv2';
import * as yaml from 'js-yaml';
import _ from 'lodash';
import {FeatureService} from "@netfoundry-ui/shared/services";

const pageSize = 500;

@Component({
    selector: 'app-download-entities',
    templateUrl: './download-entities.component.html',
    styleUrls: ['./download-entities.component.scss'],
    standalone: false
})
export class DownloadEntitiesComponent {
    isLoading = false;
    hideHelp = false;
    asCreate = false;
    embedAll = true;
    asCreateType = '';

    constructor(
        private csvDownloadService: CsvDownloadService,
        private platformServiceService: PlatformServiceService,
        private dialogRef: MatDialogRef<DownloadEntitiesComponent>,
        public featureService: FeatureService,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {}

    downloadJSON() {
        this.isLoading = true;
        if (this.data.downloadAll) {
            this._downloadAll('application/json');
        } else if (this.data.downloadNetwork) {
            this._downloadNetwork('application/json');
        } else if(this.asCreate && this.data.fetchSelected) {
            this._fetchSelected('application/json');
        } else {
            const items = this.asCreate ? this.data.asCreateItems : this.data.items;
            const beautifiedJson = JSON.stringify(items, null, 2);
            this.csvDownloadService.downloadAll(beautifiedJson, this.data.fileName, undefined, 'application/json');
            this.dialogRef.close();
        }
    }

    downloadYAML() {
        this.isLoading = true;
        if (this.data.downloadAll) {
            this._downloadAll('application/x-yaml');
        } else if (this.data.downloadNetwork) {
            this._downloadNetwork('application/x-yaml');

        } else if(this.asCreate && this.data.fetchSelected) {
            this._fetchSelected('application/x-yaml');
        } else {
            const items = this.asCreate ? this.data.asCreateItems : this.data.items;
            const yamlText = yaml.dump(items);
            this.csvDownloadService.downloadAll(yamlText, this.data.fileName, undefined, 'application/x-yaml');
            this.isLoading = false;
            this.dialogRef.close();
        }
    }

    downloadCSV() {
        if (this.asCreate) {
            return;
        }
        this.isLoading = true;
        if (this.data.downloadAll) {
            this._downloadAll('text/csv');
        } else {
            const columns = this.asCreate ? this.data.columnsAsCreate : this.data.columns;
            this.csvDownloadService.download(
                this.data.fileName,
                this.data.items,
                columns,
                this.data.translateStatus,
                this.data.isEndpoint,
                undefined,
                false
            );
            this.isLoading = false;
            this.dialogRef.close();
        }
    }

    toggleAsCreate() {
        this.asCreate = !this.asCreate;
        this.asCreateType = this.asCreate ? ';as=create' : '';
    }

    hide() {
        this.dialogRef.close();
    }

    _getQueryParams(pageNum: number, params: HttpParams = new HttpParams()): HttpParams {
        params = params.set('size', pageSize.toString()).set('page', pageNum.toString());
        return params;
    }

    _downloadNetwork(contentType = 'application/json') {
        let networkObj: any = {};
        const networkPromise = this.data
            .entityDownloadService(
                this.data.model.id,
                `application/json${this.asCreateType}`,
                false,
                'all'
            )
            .toPromise()
            .then((result) => {
                networkObj = JSON.parse(result);
                _.unset(networkObj, '_embedded');
            });

        /*let networkServices = [];
        const servicesPromise = this.platformServiceService
            .getAllServicesByNetworkId(this.data.model.id)
            .then((results) => {
                networkServices = results;
            });*/

        Promise.all([networkPromise]).then(() => {
            let textResult = '';
            if (contentType === 'application/json') {
                textResult = JSON.stringify(networkObj, null, 2);
            } else {
                textResult = yaml.dump(networkObj);
            }

            this.csvDownloadService.downloadAll(textResult, this.data.fileName, {}, contentType);
            this.dialogRef.close();
        });
    }

    _downloadAll(contentType = 'application/json') {
        const pageResults = [];
        const promiseResults = [];
        let pages = 1;
        if (pageSize < this.data.totalRows) {
            pages = _.ceil(this.data.totalRows / pageSize);
        }

        for (let i = 0; i < pages; i++) {
            const queryParams: HttpParams = this._getQueryParams(i, this.data.params);
            promiseResults.push(
                this.data
                    .entityDownloadService(
                        this.data.currentNetwork.id,
                        `${contentType}${this.asCreateType}`,
                        queryParams
                    )
                    .toPromise()
                    .then((result) => {
                        pageResults[i] = result;
                    })
            );
        }

        Promise.all(promiseResults).then(
            () => {
                let result = [];
                let textResult = '';
                let headerText;
                for (const pageResult of pageResults) {
                    if (contentType === 'application/json') {
                        result = [...result, ...JSON.parse(pageResult)];
                    } else {
                        const lines = pageResult.split('\n');
                        headerText = lines.splice(0, 1);
                        let spacer = '';
                        if (contentType === 'application/x-yaml') {
                            spacer = '\n';
                        }
                        textResult += lines.join('\n') + spacer;
                    }
                }
                if (contentType === 'application/json') {
                    textResult = JSON.stringify(result, null, 2);
                } else {
                    textResult = headerText + '\n' + textResult;
                }
                const options = contentType === 'text/csv' ? { isEndpoint: this.data.entityType === 'Endpoints' } : {};

                this.csvDownloadService.downloadAll(textResult, this.data.fileName, options, contentType);
                this.dialogRef.close();
            },
            () => {
                this.isLoading = false;
            }
        );
    }
    
    _fetchSelected(contentType = 'application/json') {
        const items = this.data.items;

        let queryParams: HttpParams = this._getQueryParams(0, this.data.params);

        let idString = "";
        if(items.length > 0) {
            for(const item of items) {
                idString += item.id + ",";
            }
            idString = idString.substring(0, idString.length-1);
            queryParams = queryParams.set("id", idString);
        }
        
        this.data
            .entityDownloadService(
                this.data.currentNetwork.id,
                `${contentType}${this.asCreateType}`,
                queryParams
            )
            .toPromise()
            .then((result) => {
                
                let textResult = '';
                let headerText;

                if (contentType === 'application/json') {
                    result = JSON.parse(result);
                } else {
                    const lines = result.split('\n');
                    headerText = lines.splice(0, 1);
                    let spacer = '';
                    if (contentType === 'application/x-yaml') {
                        spacer = '\n';
                    }
                    textResult += lines.join('\n') + spacer;
                }
                if (contentType === 'application/json') {
                    textResult = JSON.stringify(result, null, 2);
                } else {
                    textResult = headerText + '\n' + textResult;
                }
                const options = contentType === 'text/csv' ? { isEndpoint: this.data.entityType === 'Endpoints' } : {};

                this.csvDownloadService.downloadAll(textResult, this.data.fileName, options, contentType);
                this.dialogRef.close();
            });
    }
}
